import { useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Breakpoint
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Error from '@mui/icons-material/Error'
import Warning from '@mui/icons-material/Warning'
import Info from '@mui/icons-material/Info'

type DialogType = 'success' | 'error' | 'warning' | 'info'

interface InfoDialogState {
  open: boolean
  type: DialogType
  title: string
  message: React.ReactNode
  onClose?: () => void
  btnSlot?: React.ReactNode
  confirmText?: string
  maxWidth?: Breakpoint | false
  width?: string
}

const iconMap = {
  success: <CheckCircle style={{ color: '#4CAF50', fontSize: 60 }} />,
  error: <Error style={{ color: '#F44336', fontSize: 60 }} />,
  warning: <Warning style={{ color: '#FF9800', fontSize: 60 }} />,
  info: <Info style={{ color: '#002678', fontSize: 60 }} />
}

const InfoDialogManager = ({
  state,
  closeDialog
}: {
  state: InfoDialogState
  closeDialog: () => void
}) => {
  return ReactDOM.createPortal(
    <Dialog
      open={state.open}
      onClose={state.onClose ?? closeDialog}
      maxWidth={state.width ? undefined : (state.maxWidth ?? 'xs')}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          overflowY: 'visible',
          ...(state.width ? { width: state.width } : {})
        }
      }}>
      <DialogTitle>
        <Typography
          sx={{
            fontWeight: '600',
            fontSize: '20px',
            textAlign: 'center',
            mt: 2,
            lineHeight: '1.6'
          }}>
          {state.title}
        </Typography>
      </DialogTitle>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          height: '60px',
          width: '60px',
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        {iconMap[state.type]}
      </Box>
      <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
        {typeof state.message === 'string' ? (
          <Typography variant='body2'>{state.message}</Typography>
        ) : (
          state.message
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        {state.btnSlot ? (
          state.btnSlot
        ) : (
          <Button
            onClick={closeDialog}
            variant='contained'
            sx={{ width: '200px', height: '36px', alignSelf: 'center', mb: 2 }}>
            {state.confirmText || 'OK'}
          </Button>
        )}
      </DialogActions>
    </Dialog>,
    document.body
  )
}

const InfoDialog = {
  _setState: (() => {}) as React.Dispatch<
    React.SetStateAction<InfoDialogState>
  >,

  open: ({
    type,
    title,
    message,
    onClose,
    btnSlot,
    maxWidth,
    width,
    confirmText
  }: {
    type: DialogType
    title: string
    message: React.ReactNode
    onClose?: () => void
    btnSlot?: React.ReactNode
    maxWidth?: Breakpoint | false
    width?: string
    confirmText?: string
  }) => {
    InfoDialog._setState({
      open: true,
      type,
      title,
      message,
      onClose,
      btnSlot,
      maxWidth,
      width,
      confirmText
    })
  },

  close: () => {
    InfoDialog._setState(prev => ({ ...prev, open: false }))
  },

  Provider: () => {
    const [state, setState] = useState<InfoDialogState>({
      open: false,
      type: 'success',
      title: '',
      message: '',
      onClose: undefined,
      btnSlot: null,
      width: 'xs',
      confirmText: 'OK'
    })

    InfoDialog._setState = setState

    return (
      <InfoDialogManager state={state} closeDialog={() => InfoDialog.close()} />
    )
  }
}

export default InfoDialog
